import {
    OutreachStatusFilter,
    ProcessStatusGroup,
    RecruitingStatusFilter,
    SortIndexField,
} from '__generated__/graphql'

export type StarRatingRange = { gte: number; lte: number } | undefined
export type DateRange = { gte?: string; lte?: string } | undefined

export type PaginationState = {
    page: number
    limit: number
}

export type CandidateFilterState = {
    colors: string[]
    processStatus: string[]
    processStatusGroup: ProcessStatusGroup
    name: string | undefined
    email: string | undefined
    phone: string | undefined
    recruitingStatus: RecruitingStatusFilter[]
    outreachStatus: OutreachStatusFilter[]
    starRating: StarRatingRange
    screeningNoteTemplateIds: string[]
    screeningNoteStarRating: StarRatingRange
    companyIds: string[]
    companyTimeFrame: string | undefined
    companyDateRange: DateRange
    collegeIds: string[]
}

export type UrlState = PaginationState & CandidateFilterState

export const DEFAULT_PAGE = 1
export const DEFAULT_LIMIT = 50

export const getViewFromTab = (
    tab: ProcessStatusGroup
):
    | 'client-portal-submitted'
    | 'client-portal-in-process'
    | 'client-portal-out-of-process' => {
    switch (tab) {
        case ProcessStatusGroup.Submitted:
            return 'client-portal-submitted'
        case ProcessStatusGroup.InProcess:
            return 'client-portal-in-process'
        case ProcessStatusGroup.OutOfProcess:
            return 'client-portal-out-of-process'
        default:
            return 'client-portal-submitted'
    }
}

export const processStatusGroupToString = (
    group: ProcessStatusGroup
): string => {
    switch (group) {
        case ProcessStatusGroup.Submitted:
            return 'Submitted'
        case ProcessStatusGroup.InProcess:
            return 'In Process'
        case ProcessStatusGroup.OutOfProcess:
            return 'Out of Process'
        default:
            return 'Submitted'
    }
}

export const mapSortIndexFieldToString = (field: SortIndexField): string => {
    switch (field) {
        case SortIndexField.FirstName:
            return 'firstName'
        case SortIndexField.LastName:
            return 'lastName'
        case SortIndexField.PreferredName:
            return 'preferredName'
        case SortIndexField.StarRating:
            return 'starRating'
        case SortIndexField.ProcessStatus:
            return 'processStatus'
        case SortIndexField.ProcessStatusUpdatedAt:
            return 'processStatusUpdatedAt'
        case SortIndexField.DateOnRoster:
            return 'dateOnRoster'
        default:
            return 'name'
    }
}

export const hasFilters = (state: CandidateFilterState): boolean => {
    return (
        state.colors.length > 0 ||
        state.processStatus.length > 0 ||
        state.recruitingStatus.length > 0 ||
        state.outreachStatus.length > 0 ||
        !!state.name ||
        !!state.email ||
        !!state.phone ||
        state.starRating !== undefined ||
        state.screeningNoteTemplateIds.length > 0 ||
        state.screeningNoteStarRating !== undefined ||
        state.companyIds.length > 0 ||
        !!state.companyTimeFrame ||
        state.companyDateRange !== undefined ||
        state.collegeIds.length > 0
    )
}

const equalStringSets = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false
    const as = [...a].sort()
    const bs = [...b].sort()
    for (let i = 0; i < as.length; i++) {
        if (as[i] !== bs[i]) return false
    }
    return true
}

export const areFiltersEqual = (
    a: CandidateFilterState,
    b: CandidateFilterState
): boolean => {
    return (
        equalStringSets(a.colors, b.colors) &&
        equalStringSets(a.processStatus, b.processStatus) &&
        a.processStatusGroup === b.processStatusGroup &&
        equalStringSets(a.recruitingStatus, b.recruitingStatus) &&
        equalStringSets(a.outreachStatus, b.outreachStatus) &&
        a.name === b.name &&
        a.email === b.email &&
        a.phone === b.phone &&
        a.starRating?.gte === b.starRating?.gte &&
        a.starRating?.lte === b.starRating?.lte &&
        equalStringSets(
            a.screeningNoteTemplateIds,
            b.screeningNoteTemplateIds
        ) &&
        a.screeningNoteStarRating?.gte === b.screeningNoteStarRating?.gte &&
        a.screeningNoteStarRating?.lte === b.screeningNoteStarRating?.lte &&
        equalStringSets(a.companyIds, b.companyIds) &&
        a.companyTimeFrame === b.companyTimeFrame &&
        a.companyDateRange?.gte === b.companyDateRange?.gte &&
        a.companyDateRange?.lte === b.companyDateRange?.lte &&
        equalStringSets(a.collegeIds, b.collegeIds)
    )
}

export const parseStarRating = (
    min?: string,
    max?: string
): StarRatingRange => {
    if (!min || !max) return undefined
    const gte = parseFloat(min)
    const lte = parseFloat(max)
    return !isNaN(gte) && !isNaN(lte) ? { gte, lte } : undefined
}

export const serializeStarRating = (
    range: StarRatingRange
): { min?: string; max?: string } => {
    if (!range) return {}
    return { min: range.gte.toString(), max: range.lte.toString() }
}

const mapProcessStatusLabelsToIds = (
    statusLabels: string[],
    processStatusOptions: Array<{ id: string; statusLabel: string }>
): string[] => {
    return statusLabels
        .map(
            (label) =>
                processStatusOptions.find(
                    (option) => option.statusLabel === label
                )?.id
        )
        .filter(Boolean) as string[]
}

export const toGraphQLFilters = (
    state: CandidateFilterState,
    processStatusOptions: Array<{ id: string; statusLabel: string }>,
    processStatusGroup?: string
) => ({
    colorNames: state.colors.length > 0 ? state.colors : undefined,
    processStatusIds:
        state.processStatus.length > 0
            ? mapProcessStatusLabelsToIds(
                  state.processStatus,
                  processStatusOptions
              )
            : undefined,
    name: state.name,
    email: state.email,
    phone: state.phone,
    recruitingStatuses:
        state.recruitingStatus.length > 0 ? state.recruitingStatus : undefined,
    outreachStatuses:
        state.outreachStatus.length > 0 ? state.outreachStatus : undefined,
    starRatingGte: state.starRating?.gte,
    starRatingLte: state.starRating?.lte,
    screeningNoteTemplateIds:
        state.screeningNoteTemplateIds.length > 0
            ? state.screeningNoteTemplateIds
            : undefined,
    screeningNoteStarRatingGte: state.screeningNoteStarRating?.gte,
    screeningNoteStarRatingLte: state.screeningNoteStarRating?.lte,
    companyIds: state.companyIds.length > 0 ? state.companyIds : undefined,
    companyTimeFrame: state.companyTimeFrame,
    companyDateRange: state.companyDateRange
        ? { gte: state.companyDateRange.gte, lte: state.companyDateRange.lte }
        : undefined,
    collegeIds: state.collegeIds.length > 0 ? state.collegeIds : undefined,
    processStatusGroup: processStatusGroup || undefined,
})

export const parseUrlParams = (searchParams: URLSearchParams): UrlState => {
    const page = parseInt(searchParams.get('page') || '1', 10)
    const limit = parseInt(searchParams.get('limit') || '50', 10)
    const colorValues =
        searchParams.get('colors')?.split(',').filter(Boolean) || []
    const processStatusLabels =
        searchParams.get('processStatus')?.split(',').filter(Boolean) || []
    const name = searchParams.get('name') || undefined
    const recruitingStatus =
        (searchParams
            .get('recruitingStatus')
            ?.split(',')
            .filter(Boolean) as RecruitingStatusFilter[]) || []
    const outreachStatus =
        (searchParams
            .get('outreachStatus')
            ?.split(',')
            .filter(Boolean) as OutreachStatusFilter[]) || []
    const starRating = parseStarRating(
        searchParams.get('starRatingMin') || undefined,
        searchParams.get('starRatingMax') || undefined
    )

    const screeningNoteTemplateIds =
        searchParams
            .get('screeningNoteTemplateIds')
            ?.split(',')
            .filter(Boolean) || []
    const screeningNoteStarRating = parseStarRating(
        searchParams.get('screeningNoteStarRatingMin') || undefined,
        searchParams.get('screeningNoteStarRatingMax') || undefined
    )
    const companyIds =
        searchParams.get('companyIds')?.split(',').filter(Boolean) || []
    const companyTimeFrame = searchParams.get('companyTimeFrame') || undefined
    const companyDateRangeGte =
        searchParams.get('companyDateRangeGte') || undefined
    const companyDateRangeLte =
        searchParams.get('companyDateRangeLte') || undefined
    const companyDateRange =
        companyDateRangeGte || companyDateRangeLte
            ? { gte: companyDateRangeGte, lte: companyDateRangeLte }
            : undefined

    const email = searchParams.get('email') || undefined
    const phone = searchParams.get('phone') || undefined
    const collegeIds =
        searchParams.get('collegeIds')?.split(',').filter(Boolean) || []

    const processStatusGroupParam = searchParams.get(
        'processStatusGroup'
    ) as ProcessStatusGroup

    return {
        page: !isNaN(page) && page > 0 ? page : DEFAULT_PAGE,
        limit: !isNaN(limit) && limit > 0 ? limit : DEFAULT_LIMIT,
        colors: colorValues,
        processStatus: processStatusLabels,
        processStatusGroup: Object.values(ProcessStatusGroup).includes(
            processStatusGroupParam
        )
            ? processStatusGroupParam
            : ProcessStatusGroup.Submitted,
        name,
        email,
        phone,
        recruitingStatus,
        outreachStatus,
        starRating,
        screeningNoteTemplateIds,
        screeningNoteStarRating,
        companyIds,
        companyTimeFrame,
        companyDateRange,
        collegeIds,
    }
}

export const buildSearchParams = (state: UrlState): URLSearchParams => {
    const params = new URLSearchParams()
    params.set('page', state.page.toString())
    params.set('limit', state.limit.toString())
    if (state.colors.length > 0) params.set('colors', state.colors.join(','))
    if (state.processStatus.length > 0)
        params.set('processStatus', state.processStatus.join(','))
    if (state.name) params.set('name', state.name)
    if (state.recruitingStatus.length > 0)
        params.set('recruitingStatus', state.recruitingStatus.join(','))
    if (state.outreachStatus.length > 0)
        params.set('outreachStatus', state.outreachStatus.join(','))

    const starRatingParams = serializeStarRating(state.starRating)
    if (starRatingParams.min) params.set('starRatingMin', starRatingParams.min)
    if (starRatingParams.max) params.set('starRatingMax', starRatingParams.max)

    if (state.screeningNoteTemplateIds.length > 0)
        params.set(
            'screeningNoteTemplateIds',
            state.screeningNoteTemplateIds.join(',')
        )
    const screeningNoteStarRatingParams = serializeStarRating(
        state.screeningNoteStarRating
    )
    if (screeningNoteStarRatingParams.min)
        params.set(
            'screeningNoteStarRatingMin',
            screeningNoteStarRatingParams.min
        )
    if (screeningNoteStarRatingParams.max)
        params.set(
            'screeningNoteStarRatingMax',
            screeningNoteStarRatingParams.max
        )

    if (state.companyIds.length > 0)
        params.set('companyIds', state.companyIds.join(','))
    if (state.companyTimeFrame)
        params.set('companyTimeFrame', state.companyTimeFrame)
    if (state.companyDateRange?.gte)
        params.set('companyDateRangeGte', state.companyDateRange.gte)
    if (state.companyDateRange?.lte)
        params.set('companyDateRangeLte', state.companyDateRange.lte)

    if (state.email) params.set('email', state.email)
    if (state.phone) params.set('phone', state.phone)
    if (state.collegeIds.length > 0)
        params.set('collegeIds', state.collegeIds.join(','))

    params.set('processStatusGroup', state.processStatusGroup)

    return params
}

export const isUrlInSyncWithState = (
    searchParams: URLSearchParams,
    state: UrlState
): boolean => {
    const url = parseUrlParams(searchParams)
    return (
        url.page === state.page &&
        url.limit === state.limit &&
        equalStringSets(url.colors, state.colors) &&
        equalStringSets(url.processStatus, state.processStatus) &&
        equalStringSets(url.recruitingStatus, state.recruitingStatus) &&
        equalStringSets(url.outreachStatus, state.outreachStatus) &&
        url.name === state.name &&
        url.email === state.email &&
        url.phone === state.phone &&
        url.starRating?.gte === state.starRating?.gte &&
        url.starRating?.lte === state.starRating?.lte &&
        equalStringSets(
            url.screeningNoteTemplateIds,
            state.screeningNoteTemplateIds
        ) &&
        url.screeningNoteStarRating?.gte ===
            state.screeningNoteStarRating?.gte &&
        url.screeningNoteStarRating?.lte ===
            state.screeningNoteStarRating?.lte &&
        equalStringSets(url.companyIds, state.companyIds) &&
        url.companyTimeFrame === state.companyTimeFrame &&
        url.companyDateRange?.gte === state.companyDateRange?.gte &&
        url.companyDateRange?.lte === state.companyDateRange?.lte &&
        equalStringSets(url.collegeIds, state.collegeIds) &&
        url.processStatusGroup === state.processStatusGroup
    )
}
